import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const sizeClasses = {
  xs: 'w-3 h-3',
  '3.5': 'w-3.5 h-3.5',
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
  xl: 'w-7 h-7',
  '2xl': 'w-8 h-8',
  '3xl': 'w-10 h-10',
  '4xl': 'w-12 h-12',
} as const;

type SpinnerSize = keyof typeof sizeClasses;

const paddingClasses = {
  6: 'py-6',
  8: 'py-8',
  10: 'py-10',
  12: 'py-12',
  16: 'py-16',
  20: 'py-20',
  24: 'py-24',
  40: 'py-40',
} as const;

interface SpinnerProps {
  size?: SpinnerSize;
  className?: string;
}

/** 인라인 스피너 아이콘 (버튼 안 등). 마진은 className으로 전달. */
export function Spinner({ size = 'sm', className }: SpinnerProps) {
  return <Loader2 className={cn(sizeClasses[size], 'animate-spin', className)} aria-hidden="true" />;
}

interface LoadingBlockProps {
  /** 스피너 아이콘 크기 */
  size?: SpinnerSize;
  /** 상하 padding (Tailwind py-* 스케일 숫자). 기본 12 */
  py?: keyof typeof paddingClasses;
  /** 스피너 옆/아래 보조 텍스트 */
  label?: string;
  /** label을 스피너 옆(가로)에 둘지, 아래(세로)에 둘지 */
  labelPosition?: 'inline' | 'below';
  /** 스피너 아이콘 색상 등 추가 클래스. 기본 text-primary */
  iconClassName?: string;
  className?: string;
}

/**
 * 페이지/섹션 단위 로딩 상태에 쓰는 공용 블록.
 * 기존에 각 페이지마다 <div className="flex justify-center items-center py-N">
 * <Loader2 .../></div> 형태로 반복 작성되던 패턴을 대체한다.
 */
export function LoadingBlock({
  size = 'lg',
  py = 12,
  label,
  labelPosition = 'inline',
  iconClassName = 'text-primary',
  className,
}: LoadingBlockProps) {
  return (
    <div
      className={cn(
        'flex items-center justify-center text-gray-500',
        labelPosition === 'below' && 'flex-col gap-2',
        paddingClasses[py],
        className
      )}
      role="status"
      aria-live="polite"
    >
      <Spinner size={size} className={cn(iconClassName, labelPosition === 'inline' && label && 'mr-2')} />
      {label && <span className="text-sm">{label}</span>}
    </div>
  );
}
